import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button, Card } from '@ecom/ui-kit';
import { type ReactElement, useRef } from 'react';
import { Link } from 'react-router-dom';

import {
  buildPublicShopUrl,
  buildShareLink,
  normalizeStorefrontBaseUrl,
  type ShareNetwork,
} from '../share-links';
import { resolveShareState, type ShareState } from '../share-state';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { withUtm } from '@/features/orders/attribution';
import { useProductsQuery } from '@/features/products/api';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { useMyStoreQuery } from '@/features/store/api';
import { env } from '@/lib/env';

const NETWORKS: readonly { readonly network: ShareNetwork; readonly labelKey: MessageKey }[] = [
  { network: 'whatsapp', labelKey: 'merchant.share.whatsapp' },
  { network: 'facebook', labelKey: 'merchant.share.facebook' },
  { network: 'telegram', labelKey: 'merchant.share.telegram' },
];

interface HelpCopy {
  readonly titleKey: MessageKey;
  readonly textKey: MessageKey;
  readonly cta?: { readonly labelKey: MessageKey; readonly to: string };
}

/** The ONE next step for each state in which the shop is not yet public. */
const HELP: Partial<Record<ShareState, HelpCopy>> = {
  'no-store': {
    titleKey: 'merchant.share.noStore.title',
    textKey: 'merchant.share.noStore.text',
    cta: { labelKey: 'merchant.share.noStore.cta', to: '/store' },
  },
  pending: {
    titleKey: 'merchant.share.pending.title',
    textKey: 'merchant.share.pending.text',
  },
  suspended: {
    titleKey: 'merchant.share.suspended.title',
    textKey: 'merchant.share.suspended.text',
  },
  refused: {
    titleKey: 'merchant.share.refused.title',
    textKey: 'merchant.share.refused.text',
  },
  'no-product': {
    titleKey: 'merchant.share.noProduct.title',
    textKey: 'merchant.share.noProduct.text',
    cta: { labelKey: 'merchant.share.noProduct.cta', to: '/products/new' },
  },
  unpublished: {
    titleKey: 'merchant.share.unpublished.title',
    textKey: 'merchant.share.unpublished.text',
    cta: { labelKey: 'merchant.share.unpublished.cta', to: '/products' },
  },
};

/** `execCommand('copy')` on the current selection — deprecated, but the only copy some in-app browsers allow. */
function legacyCopy(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

const LINK_BUTTON = 'ecom-button ecom-button--secondary ecom-button--sm';

/** Where merchants usually put a link to their shop (profile « bio », page button…). */
const BIO_LINKS = ['instagram', 'tiktok', 'facebook', 'youtube'] as const;
const BIO_LABELS: Readonly<Record<(typeof BIO_LINKS)[number], string>> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  youtube: 'YouTube',
};

/**
 * « Partager ma boutique » — the public address of the shop, with copy / open / WhatsApp / Facebook / Telegram.
 *
 * Reads only what the dashboard already fetches (the shop, the memberships, the product list): it adds no endpoint. The
 * link appears only when the shop is really public (see `resolveShareState`); until then a single, short help tells the
 * merchant the next step. While anything loads, or if a read fails, it shows NOTHING rather than a wrong prompt.
 *
 * `context="store"` is the permanent home on the shop page, where the form is already right below: it shows the link
 * when ready and stays silent otherwise. `context="dashboard"` also shows the next-step help.
 */
export function ShareStoreCard({
  context,
}: {
  readonly context: 'dashboard' | 'store';
}): ReactElement | null {
  const { t, locale } = useI18n();
  const toast = useToast();
  const shop = useActiveShop();
  const storeQuery = useMyStoreQuery();
  const productsQuery = useProductsQuery();
  const inputRef = useRef<HTMLInputElement>(null);

  const state = resolveShareState({
    store: storeQuery.data,
    storeFailed: storeQuery.isError,
    tenantStatus: shop.activeMembership?.tenantStatus,
    products: productsQuery.data,
    productsFailed: productsQuery.isError,
  });

  if (state === 'loading' || state === 'unavailable') return null;

  if (state !== 'ready') {
    const help = HELP[state];
    if (context === 'store' || help === undefined) return null;
    return (
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-ink">{t(help.titleKey)}</p>
          <p className="text-sm text-inkSoft">{t(help.textKey)}</p>
        </div>
        {help.cta !== undefined ? (
          <Link to={help.cta.to} className="shrink-0">
            <Button variant="secondary">{t(help.cta.labelKey)}</Button>
          </Link>
        ) : null}
      </Card>
    );
  }

  const store = storeQuery.data;
  const url = buildPublicShopUrl(
    normalizeStorefrontBaseUrl(env.storefrontBaseUrl),
    locale,
    store?.slug,
  );
  // The deployment did not say where the storefront lives: no link is better than a wrong one.
  if (store === null || store === undefined || url === null) return null;

  async function copy(link: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('merchant.share.copied'));
    } catch {
      // No clipboard permission (or an insecure page): select the link and try the older copy command; if that is
      // refused too, the link stays selected so a manual copy is one gesture.
      inputRef.current?.select();
      if (legacyCopy()) toast.success(t('merchant.share.copied'));
      else toast.error(t('merchant.share.copyFailed'));
    }
  }

  const message = t('merchant.share.message', { name: store.name });
  const newTab = t('merchant.share.newTab');

  return (
    <Card className="flex flex-col gap-4 p-5" aria-labelledby="share-store-title">
      <div>
        <h2 id="share-store-title" className="font-display text-base font-bold text-ink">
          {t('merchant.share.readyTitle')}
        </h2>
        <p className="text-sm text-ink">{t('merchant.share.readySubtitle')}</p>
      </div>

      <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
        <input
          ref={inputRef}
          type="text"
          readOnly
          dir="ltr"
          value={url}
          aria-label={t('merchant.share.linkLabel')}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
          className="min-w-0 flex-1 rounded-md border border-line bg-surface px-3 py-2 font-mono text-sm text-ink"
        />
        <div className="flex shrink-0 gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              void copy(url);
            }}
          >
            {t('merchant.share.copy')}
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer" className={LINK_BUTTON}>
            <span>{t('merchant.share.open')}</span>
            <span className="ecom-sr-only">{newTab}</span>
          </a>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-inkSoft">{t('merchant.share.shareOn')}</span>
        {NETWORKS.map(({ network, labelKey }) => (
          <a
            key={network}
            href={buildShareLink(network, withUtm(url, network), message)}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_BUTTON}
          >
            <span>{t(labelKey)}</span>
            <span className="ecom-sr-only">{newTab}</span>
          </a>
        ))}
      </div>

      {/* Migration 0097 — tagged links: an order placed after a visit through one of them is shown « via … ». */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">{t('merchant.attribution.bioLinks')}</span>
        <p className="text-xs text-inkSoft">{t('merchant.attribution.bioLinksHint')}</p>
        <div className="flex flex-wrap gap-2">
          {BIO_LINKS.map((source) => (
            <Button
              key={source}
              variant="secondary"
              size="sm"
              onClick={() => {
                void copy(withUtm(url, source, 'social', 'bio'));
              }}
            >
              {t('merchant.attribution.copyFor', { source: BIO_LABELS[source] })}
            </Button>
          ))}
        </div>
      </div>
    </Card>
  );
}
